import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router";
import type { StudySummaryDto } from "@gazelab/shared";
import { api, errorMessage } from "../../services/api.js";
import { useAsync } from "../../lib/useAsync.js";
import { fmtDate, STUDY_STATUS_LABELS } from "../../lib/format.js";
import { Badge, ConfirmDialog, ErrorAlert, Modal, PageHeader, Spinner } from "../../components/ui/index.js";
import { DEMO_LOCK_NOTE, useDemo } from "./AdminLayout.js";

export function StudiesPage() {
  const [showArchived, setShowArchived] = useState(false);
  const studies = useAsync(() => api.listStudies(showArchived), [showArchived]);
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [toDelete, setToDelete] = useState<StudySummaryDto | null>(null);
  const [toArchive, setToArchive] = useState<StudySummaryDto | null>(null);
  const navigate = useNavigate();
  const locked = useDemo()?.enabled === true;

  async function create(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      const s = await api.createStudy({
        title: title.trim(),
        description: description.trim(),
      });
      setCreating(false);
      setTitle("");
      setDescription("");
      navigate(`/estudios/${s.id}/configuracion`);
    } catch (err) {
      setError(errorMessage(err));
    }
  }

  async function act(fn: () => Promise<unknown>) {
    setError(null);
    try {
      await fn();
      await studies.reload();
    } catch (err) {
      setError(errorMessage(err));
    }
  }

  return (
    <div>
      <PageHeader title="Estudios" description="Crea, edita, duplica o archiva estudios. Los grupos predeterminados son 20–29, 30–39 y 40–49 años (editables).">
        <label className="inline-flex items-center gap-2 text-sm text-slate-700" data-tour="studies-archived">
          <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
          Mostrar archivados
        </label>
        <button type="button" className="btn-primary" onClick={() => setCreating(true)} data-tour="studies-new">
          Nuevo estudio
        </button>
      </PageHeader>
      <ErrorAlert error={error ?? studies.error} />
      {studies.loading && !studies.data ? (
        <Spinner />
      ) : (
        <div className="mt-4 grid gap-3">
          {(studies.data ?? []).length === 0 && <p className="text-sm text-slate-600">No hay estudios todavía.</p>}
          {(studies.data ?? []).map((s, i) => (
            <div key={s.id} className="card flex flex-wrap items-center justify-between gap-3" data-tour={i === 0 ? "studies-card" : undefined}>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <Link to={`/estudios/${s.id}/configuracion`} className="text-lg font-semibold text-sky-800 hover:underline">
                    {s.title}
                  </Link>
                  <Badge tone={s.status === "active" ? "success" : s.status === "archived" ? "neutral" : "info"}>{STUDY_STATUS_LABELS[s.status]}</Badge>
                  {s.sessionCounts.synthetic.total > 0 && <Badge tone="synthetic">Incluye demo SINTÉTICA</Badge>}
                </div>
                <p className="mt-1 line-clamp-2 text-sm text-slate-600">{s.description || "Sin descripción"}</p>
                <p className="mt-1 text-xs text-slate-500">
                  Código {s.code} · {s.imageCount - s.videoCount} imágenes · {s.videoCount} videos · {s.groupCount} grupos · {s.sessionCounts.webgazer.total} sesiones reales · actualizado{" "}
                  {fmtDate(s.updatedAt)}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Link to={`/estudios/${s.id}/resultados`} className="btn-secondary">
                  Resultados
                </Link>
                <button type="button" className="btn-secondary" onClick={() => act(() => api.duplicateStudy(s.id))}>
                  Duplicar protocolo
                </button>
                {s.status !== "archived" ? (
                  <button type="button" className="btn-secondary" onClick={() => setToArchive(s)}>
                    Archivar
                  </button>
                ) : (
                  <button type="button" className="btn-secondary" onClick={() => act(() => api.updateStudy(s.id, { status: "draft" }))}>
                    Restaurar a borrador
                  </button>
                )}
                <button type="button" className="btn-danger" onClick={() => setToDelete(s)} disabled={locked} title={locked ? DEMO_LOCK_NOTE : undefined}>
                  Eliminar
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        open={creating}
        title="Nuevo estudio"
        onClose={() => setCreating(false)}
        footer={
          <>
            <button type="button" className="btn-secondary" onClick={() => setCreating(false)}>
              Cancelar
            </button>
            <button type="submit" form="create-study" className="btn-primary" disabled={!title.trim()}>
              Crear
            </button>
          </>
        }
      >
        <form id="create-study" onSubmit={create} className="space-y-3">
          <div>
            <label htmlFor="study-title" className="label">
              Nombre
            </label>
            <input id="study-title" className="input" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} required />
          </div>
          <div>
            <label htmlFor="study-desc" className="label">
              Descripción
            </label>
            <textarea id="study-desc" className="input" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={5000} />
          </div>
          <p className="text-xs text-slate-500">
            Se crearán los grupos 20–29, 30–39 y 40–49 años y el protocolo predeterminado (5 s por imagen, cruz de 1 s, 9 puntos de calibración). El estudio
            puede combinar imágenes y videos. Podrás cambiarlos después.
          </p>
        </form>
      </Modal>

      <ConfirmDialog
        open={toArchive !== null}
        title="Archivar estudio"
        message={<p>El estudio «{toArchive?.title}» dejará de aceptar participantes y se ocultará de la lista. Sus datos se conservan.</p>}
        confirmLabel="Archivar"
        onCancel={() => setToArchive(null)}
        onConfirm={async () => {
          const s = toArchive!;
          setToArchive(null);
          await act(() => api.updateStudy(s.id, { status: "archived" }));
        }}
      />
      <ConfirmDialog
        open={toDelete !== null}
        title="Eliminar estudio definitivamente"
        danger
        requireText={toDelete?.title}
        message={
          <p>
            Se eliminarán el estudio, sus imágenes, grupos, ROIs y <strong>todas las sesiones y muestras de mirada</strong>. No se puede deshacer.
          </p>
        }
        confirmLabel="Eliminar todo"
        onCancel={() => setToDelete(null)}
        onConfirm={async (text) => {
          const s = toDelete!;
          setToDelete(null);
          await act(() => api.deleteStudy(s.id, text));
        }}
      />
    </div>
  );
}
